import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

/**
 * Layout route that guards all authenticated pages.
 *
 * Usage in App.tsx:
 *   <Route element={<PrivateRoute />}>
 *     <Route path="/dashboard" element={<Dashboard />} />
 *   </Route>
 *
 * If no `srs_user` session marker is found in localStorage the user is sent to
 * /login. The original destination is preserved via React Router's `replace`
 * so pressing Back does not loop back to the guarded page.
 *
 * ⚠️ SECURITY NOTE — these guards (PrivateRoute / TenantRoute / LandlordRoute /
 * RoleRoute) are UX-ONLY. The `srs_user` object in localStorage is client-side
 * and user-editable, so it must NEVER be treated as an authorization boundary.
 * Real authorization is enforced server-side: every protected API route sits
 * behind `auth:api` (HttpOnly `srs_token` cookie) plus `role:` / ownership
 * middleware, so forging `srs_user` only renders an empty UI shell — it grants
 * access to no data. Do not add data-exposing logic that trusts these guards.
 */
// Role constants — must match backend HasRoleCheck trait
const TENANT_ROLE = 'tenant';
const LANDLORD_ROLES = ['landlord', 'agent', 'agency'];

export function getUserRoles(): string[] {
  try {
    const stored = localStorage.getItem('srs_user');
    const user = stored ? JSON.parse(stored) : null;
    return (user?.roles ?? []).map((r: { name?: string; role_name?: string }) =>
      (r.name ?? r.role_name ?? '').toLowerCase()
    );
  } catch { return []; }
}

/** Guards all authenticated pages — redirects to /login if no session. */
export default function PrivateRoute({ children }: { children?: React.ReactNode }) {
  const location = useLocation();
  let session: unknown = null;
  try { session = JSON.parse(localStorage.getItem('srs_user') ?? 'null'); } catch { /* invalid session */ }
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search + location.hash }} />;
  }
  if (children) {
    return <>{children}</>;
  }
  return <Outlet />;
}

/** Guards routes that require specific roles — redirects to /dashboard if role not met. */
export function RoleRoute({ allowed }: { allowed: string[] }) {
  const roles = getUserRoles();
  const hasRole = allowed.some(r => roles.includes(r));
  return hasRole ? <Outlet /> : <Navigate to="/dashboard" replace />;
}

/** Guards tenant-only routes — non-tenants are sent to /dashboard. */
export function TenantRoute() {
  const roles = getUserRoles();
  return roles.includes(TENANT_ROLE) ? <Outlet /> : <Navigate to="/dashboard" replace />;
}

/** Guards landlord/agent/agency routes. */
export function LandlordRoute() {
  const roles = getUserRoles();
  return LANDLORD_ROLES.some(r => roles.includes(r)) ? <Outlet /> : <Navigate to="/dashboard" replace />;
}
